import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useEmployeeLookupSelector } from '@/hooks/employee/useEmployeeLookupSelector';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { incidentErrorTypeService, type IncidentErrorType } from '@/api/domains/catalog';
import { employeeIncidentService } from '@/api/domains/employeeIncident';
import type {
  IncidentManagementItem,
  IncidentManagementTotals,
  IncidentStatus,
  LC_BulkApproveIncidentsOut,
  LC_BulkRejectIncidentsOut,
} from '@/api/domains/employeeIncident';
import { formatDateToYYYYMMDD, calculateDateRange } from '@/utils';
import type { TableColumn, TableAction, BulkActionItem } from '@/components/ui/tables/Table.types';
import type { DateRangeValue } from '@/components/ui/filters/DateRangeFilter';


const ORDER_BY_MAP: Record<string, string> = {
  taxId: 'taxIdentificationNumber',
  employeeName: 'employeeFullName',
  errorType: 'errorType',
  incidentDate: 'incidentDate',
  markingDate: 'markingDate',
  markingTime: 'markingTime',
  locationName: 'locationName',
  status: 'status',
};

// `filterEmployeeId` NO forma parte de este estado a propósito: a diferencia de fecha/tipo de
// error/estado, el colaborador nunca debe persistir entre sesiones (feedback de negocio) — cada
// carga de la pantalla debe iniciar en "Todos". Se maneja como estado local aparte, fuera de
// `usePaginatedList`/`screenCode` (mismo patrón que `useEmployeeContractList`).
interface FilterState {
  dateFrom: string;
  dateTo: string;
  errorTypeId: number | null;
  status: IncidentStatus;
}

const ZERO_TOTALS: IncidentManagementTotals = {
  pendientes: 0,
  aprobados: 0,
  rechazados: 0,
  error: 0,
  todos: 0,
};

export interface UseIncidentManagementListResult {
  rows: IncidentManagementItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<IncidentManagementItem>[];
  actions: TableAction<IncidentManagementItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  isLoading: boolean;
  errorTypeOptions: { value: string; label: string }[];
  statusOptions: { value: string; label: string }[];
  formattedFilterValues: Record<string, string>;
  handleFilterChange: (key: string, value: string | string[]) => void;
  dateFrom: string;
  dateTo: string;
  defaultDateFrom: string;
  defaultDateTo: string;
  onDateRangeChange: (range: DateRangeValue) => void;
  employeeSelector: ReturnType<typeof useEmployeeLookupSelector>;
  employeeSelectorOptions: { value: string; label: string }[];
  totals: IncidentManagementTotals;
  errorTypeId: number | null;
  status: IncidentStatus;
  filterEmployeeId: number | null;
  searchText: string;
  // Bulk approval (ASIS-5249)
  bulkActions: BulkActionItem[];
  tableSelectionKey: string;
  onSelectionChange: (selected: IncidentManagementItem[]) => void;
  isRowSelectable: (row: IncidentManagementItem) => boolean;
  selectedIncidents: IncidentManagementItem[];
  isBulkApproveModalOpen: boolean;
  onBulkApproveModalClose: () => void;
  onBulkApproveSuccess: (result: LC_BulkApproveIncidentsOut) => void;
  // Rechazo masivo (ASIS-5250)
  isBulkRejectModalOpen: boolean;
  onBulkRejectModalClose: () => void;
  onBulkRejectSuccess: (result: LC_BulkRejectIncidentsOut) => void;
  // Selección "todo lo listado" cruzando páginas, acotada a los filtros vigentes (ASIS-5249)
  isSelectAllTotalActive: boolean;
  onSelectAllTotal: () => void;
  onClearSelectionTotal: () => void;
  selectAllTotalCount: number;
  excludeIncidentIds: number[];
}

export function useIncidentManagementList(): UseIncidentManagementListResult {
  const { t: translate } = useTranslation();
  const { showApiError, openSidePanel, closeSidePanel } = useUI();
  const { canEdit } = usePagePermissions();

  const [totals, setTotals] = useState<IncidentManagementTotals>(ZERO_TOTALS);
  // Preserva el label del colaborador elegido: al seleccionar desde una búsqueda,
  // cerrar el selector limpia el texto buscado y recarga la lista por defecto,
  // que puede no incluir al colaborador ya elegido (ver ASIS-5244, feedback QA).
  const [selectedEmployeeLabel, setSelectedEmployeeLabel] = useState<string | null>(null);
  // Filtro de colaborador: estado local, nunca persistido — siempre arranca en `null` ("Todos")
  // en cada carga de la pantalla, independientemente de la última selección de la sesión anterior.
  const [filterEmployeeId, setFilterEmployeeId] = useState<number | null>(null);

  const incidentErrorTypesCatalog = useCatalogLoader<IncidentErrorType>(
    (signal) => incidentErrorTypeService.getIncidentErrorTypes(signal),
    (type) => ({ value: String(type.id), label: type.description }),
    [],
  );

  const initialFilters = useMemo<FilterState>(() => {
    const defaultRange = calculateDateRange('MONTH_1', formatDateToYYYYMMDD);
    return {
      dateFrom: defaultRange.dateFrom,
      dateTo: defaultRange.dateTo,
      errorTypeId: null,
      status: 'todos',
    };
  }, []);

  // El rango de fecha tampoco debe persistir entre sesiones (mismo criterio que
  // `filterEmployeeId`): cada carga de la pantalla debe iniciar en el mes actual, aunque el
  // usuario haya dejado guardado un rango distinto (p.ej. de pruebas) en una sesión anterior.
  // `errorTypeId`/`status` sí persisten — solo se pisa `dateFrom`/`dateTo` al hidratar.
  const overrideDateRangeOnHydrate = useCallback(
    (hydrated: { filters: FilterState }): FilterState => ({
      ...hydrated.filters,
      dateFrom: initialFilters.dateFrom,
      dateTo: initialFilters.dateTo,
    }),
    [initialFilters],
  );

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<FilterState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<IncidentManagementItem> | null> => {
      const response = await employeeIncidentService.listManagement(
        {
          dateFrom: params.filters.dateFrom,
          dateTo: params.filters.dateTo,
          errorTypeId: params.filters.errorTypeId,
          status: params.filters.status === 'todos' ? null : params.filters.status,
          filterEmployeeId,
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText || null,
          orderBy: params.sort?.key ?? null,
          orderDirection: params.sort?.direction ?? null,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:incidentManagement.messages.loadError'));
        setTotals(ZERO_TOTALS);
        return null;
      }

      setTotals(response.data.totals);
      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate, filterEmployeeId],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    debouncedSearchText,
    refetch,
  } = usePaginatedList<IncidentManagementItem, FilterState>({
    fetchFn,
    initialFilters,
    initialSort: { key: 'incidentDate', direction: 'DESC' },
    orderByMap: ORDER_BY_MAP,
    screenCode: ROUTE_DEFINITIONS.INCIDENCIAS_MARCAJE.code,
    onHydrated: overrideDateRangeOnHydrate,
  });

  // `filterEmployeeId` es estado local (no persiste, ver comentario en `FilterState`), pero
  // igual puede llegar distinto de null durante la sesión si el usuario ya filtró por un
  // colaborador que no está en la primera página del lookup. Pasarlo como `initialEmployeeId`
  // hace que el hook lo resuelva y lo inyecte en `options` (misma resolución por ID que usa
  // `loadEmployeeById`), evitando que el Selector caiga al placeholder en vez de mostrar el nombre.
  const employeeSelector = useEmployeeLookupSelector({
    initialEmployeeId: filterEmployeeId !== null ? String(filterEmployeeId) : null,
  });

  // ── Aprobación masiva (ASIS-5249) ───────────────────────────────────────────
  const [selectedIncidents, setSelectedIncidents] = useState<IncidentManagementItem[]>([]);
  const [isBulkApproveModalOpen, setIsBulkApproveModalOpen] = useState(false);
  // Rechazo masivo (ASIS-5250): mismo estado de selección que la aprobación masiva
  // (selectedIncidents/isSelectAllTotalActive/excludeIncidentIds), solo cambia qué modal se abre.
  const [isBulkRejectModalOpen, setIsBulkRejectModalOpen] = useState(false);
  const [selectionVersion, setSelectionVersion] = useState(0);
  // "Seleccionar todo lo listado" cruzando páginas: al activarse, la aprobación no viaja con
  // los IDs de la página visible sino que el backend resuelve, al momento de ejecutar, todos
  // los Pendientes que cumplan los filtros vigentes (ver useIncidentBulkApproval).
  // `selectedIncidents` sigue reflejando solo la página actual — se usa únicamente para
  // calcular qué filas de esta página se deseleccionaron manualmente (excludeIncidentIds).
  const [isSelectAllTotalActive, setIsSelectAllTotalActive] = useState(false);

  const tableSelectionKey = useMemo(
    () => `${page}-${pageSize}-${selectionVersion}`,
    [page, pageSize, selectionVersion],
  );

  const onSelectionChange = useCallback((selected: IncidentManagementItem[]) => {
    setSelectedIncidents(selected);
  }, []);

  const isRowSelectable = useCallback(
    // item.status llega desde el backend ya formateado como texto de UI
    // (IncidentStatuses.ToUiStatus → "Pendiente", "Aprobado", "Rechazado", "Error"),
    // no el valor lowercase usado en los filtros.
    (row: IncidentManagementItem) => row.status === 'Pendiente',
    [],
  );

  // La tabla se remonta (vía tableSelectionKey) en cada cambio de página/tamaño de página,
  // lo que limpia su estado interno de selección — este efecto mantiene sincronizado el
  // estado del hook con ese remount (mismo patrón que useLocationList).
  useEffect(() => {
    setSelectedIncidents([]);
    setIsSelectAllTotalActive(false);
     
  }, [page, pageSize]);

  const onSelectAllTotal = useCallback(() => setIsSelectAllTotalActive(true), []);

  const onClearSelectionTotal = useCallback(() => {
    setIsSelectAllTotalActive(false);
    setSelectionVersion((version) => version + 1);
  }, []);

  // Incidencias Pendientes de la página actual deseleccionadas manualmente tras activar la
  // selección total — se envían como exclusión al backend en modo "seleccionar todo".
  const excludeIncidentIds = useMemo(() => {
    if (!isSelectAllTotalActive) return [];
    const selectedIdSet = new Set(selectedIncidents.map((row) => row.incidentId));
    return rows
      .filter((row) => row.status === 'Pendiente' && !selectedIdSet.has(row.incidentId))
      .map((row) => row.incidentId);
  }, [isSelectAllTotalActive, rows, selectedIncidents]);

  const onBulkApproveModalClose = useCallback(() => setIsBulkApproveModalOpen(false), []);

  const onBulkApproveSuccess = useCallback((_result: LC_BulkApproveIncidentsOut) => {
    setIsBulkApproveModalOpen(false);
    setSelectedIncidents([]);
    setIsSelectAllTotalActive(false);
    setSelectionVersion((version) => version + 1);
    refetch();
  }, [refetch]);

  const onBulkRejectModalClose = useCallback(() => setIsBulkRejectModalOpen(false), []);

  const onBulkRejectSuccess = useCallback((_result: LC_BulkRejectIncidentsOut) => {
    setIsBulkRejectModalOpen(false);
    setSelectedIncidents([]);
    setIsSelectAllTotalActive(false);
    setSelectionVersion((version) => version + 1);
    refetch();
  }, [refetch]);

  const bulkActions: BulkActionItem[] = useMemo(
    () =>
      canEdit
        ? [
          {
            label: translate('attendance:incidentManagement.bulk.approve'),
            icon: 'check',
            onClick: () => setIsBulkApproveModalOpen(true),
          },
          {
            label: translate('attendance:incidentManagement.bulk.reject'),
            icon: 'close',
            variant: 'danger',
            onClick: () => setIsBulkRejectModalOpen(true),
          },
        ]
        : [],
    [canEdit, translate],
  );

  const handleFilterChange = useCallback(
    (key: string, value: string | string[]) => {
      if (typeof value !== 'string') return;

      if (key === 'errorTypeId') {
        setFilters((previous) => ({ ...previous, errorTypeId: value === '0' ? null : Number(value) }));
        return;
      }

      if (key === 'status') {
        setFilters((previous) => ({ ...previous, status: value as IncidentStatus }));
        return;
      }

      if (key === 'filterEmployeeId') {
        if (value === '0') {
          setSelectedEmployeeLabel(null);
        } else {
          const found = employeeSelector.options.find((option) => option.value === value);
          setSelectedEmployeeLabel(found ? found.label : null);
        }
        // Estado local, no pasa por `setFilters` (no persiste — ver `FilterState`). Se
        // resetea la página manualmente ya que `setFilters` no hace ese reset por nosotros.
        setFilterEmployeeId(value === '0' ? null : Number(value));
        setPage(1);
      }
    },
    [setFilters, setPage, employeeSelector.options],
  );

  const onDateRangeChange = useCallback(
    (range: DateRangeValue) => {
      setFilters((previous) => ({ ...previous, dateFrom: range.from ?? '', dateTo: range.to ?? '' }));
    },
    [setFilters],
  );

  const errorTypeOptions = useMemo(
    () => [
      { value: '0', label: translate('attendance:incidentManagement.filters.allErrorTypes') },
      ...incidentErrorTypesCatalog.options,
    ],
    [incidentErrorTypesCatalog.options, translate],
  );

  const statusOptions = useMemo(
    () => [
      { value: 'todos', label: translate('attendance:incidentManagement.filters.allStatuses') },
      { value: 'pendiente', label: translate('attendance:incidentManagement.status.pendiente') },
      { value: 'aprobado', label: translate('attendance:incidentManagement.status.aprobado') },
      { value: 'rechazado', label: translate('attendance:incidentManagement.status.rechazado') },
      { value: 'error', label: translate('attendance:incidentManagement.status.error') },
    ],
    [translate],
  );

  // Los tres filtros (tipo de error, estado, colaborador) se renderizan como Selector
  // individuales dentro del panel "Filtros" (ver página), en ese orden, según Figma —
  // el colaborador requiere búsqueda paginada en servidor (useEmployeeLookupSelector),
  // que el componente genérico FilterDropdown no soporta en su lista estática de opciones.
  const formattedFilterValues = useMemo(
    () => ({
      status: filters.status,
      errorTypeId: filters.errorTypeId !== null ? String(filters.errorTypeId) : '0',
      filterEmployeeId: filterEmployeeId !== null ? String(filterEmployeeId) : '0',
    }),
    [filters.status, filters.errorTypeId, filterEmployeeId],
  );

  // Opciones del selector de colaborador con sentinel "Todos" (value '0') prepended,
  // ya que useEmployeeLookupSelector no incluye esta opción por defecto.
  // El formato del nombre ya viene resuelto desde el mapper del dominio employee.
  const employeeSelectorOptions = useMemo(() => {
    const base = [
      { value: '0', label: translate('attendance:incidentManagement.filters.allEmployees') },
      ...employeeSelector.options,
    ];

    // Si el colaborador seleccionado ya no está en la lista (p.ej. tras cerrar el
    // selector, que limpia la búsqueda y recarga la página por defecto), lo
    // reinsertamos usando el label capturado al momento de elegirlo, para que el
    // Selector siga mostrando su nombre en vez de caer al placeholder "Todos".
    const selectedId = filterEmployeeId !== null ? String(filterEmployeeId) : null;
    const isMissing = selectedId && !base.some((option) => option.value === selectedId);
    if (selectedId && selectedEmployeeLabel && isMissing) {
      return [...base, { value: selectedId, label: selectedEmployeeLabel }];
    }

    return base;
  }, [employeeSelector.options, filterEmployeeId, selectedEmployeeLabel, translate]);

  const columns: TableColumn<IncidentManagementItem>[] = useMemo(
    () => [
      {
        key: 'taxId',
        header: translate('attendance:incidentManagement.columns.taxId'),
        sortable: true,
      },
      {
        key: 'employeeName',
        header: translate('attendance:incidentManagement.columns.employee'),
        sortable: true,
      },
      {
        key: 'errorType',
        header: translate('attendance:incidentManagement.columns.errorType'),
        sortable: true,
      },
      {
        key: 'incidentDate',
        header: translate('attendance:incidentManagement.columns.incidentDate'),
        sortable: true,
      },
      {
        key: 'markingDate',
        header: translate('attendance:incidentManagement.columns.markingDate'),
        sortable: true,
      },
      {
        key: 'markingTime',
        header: translate('attendance:incidentManagement.columns.markingTime'),
        sortable: true,
      },
      {
        key: 'locationName',
        header: translate('attendance:incidentManagement.columns.location'),
        sortable: true,
      },
      {
        key: 'status',
        header: translate('attendance:incidentManagement.columns.status'),
        sortable: true,
      },
    ],
    [translate],
  );

  const handleViewDetail = useCallback(
    (row: IncidentManagementItem) => {
      openSidePanel({
        component: 'IncidentManagementDetailPanel',
        title: translate('attendance:incidentManagement.detail.title'),
        props: {
          record: row,
          // Aprobación exitosa: cierra el panel y refresca el listado (los
          // contadores/indicadores vienen de la misma respuesta del listado).
          onApproved: () => {
            closeSidePanel();
            refetch();
          },
          // Rechazo exitoso: mismo comportamiento que la aprobación (ASIS-5248).
          onRejected: () => {
            closeSidePanel();
            refetch();
          },
          // Conflicto de concurrencia: el listado quedó desactualizado, se
          // cierra el panel y se refresca igualmente.
          onConflict: () => {
            closeSidePanel();
            refetch();
          },
        },
      });
    },
    [openSidePanel, translate, closeSidePanel, refetch],
  );

  const actions: TableAction<IncidentManagementItem>[] = useMemo(
    () => [
      {
        id: 'detail',
        label: translate('common:tableActions.detail'),
        icon: 'eye',
        onClick: handleViewDetail,
      },
    ],
    [translate, handleViewDetail],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    isLoading: loading,
    errorTypeOptions,
    statusOptions,
    formattedFilterValues,
    handleFilterChange,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    defaultDateFrom: initialFilters.dateFrom,
    defaultDateTo: initialFilters.dateTo,
    onDateRangeChange,
    employeeSelector,
    employeeSelectorOptions,
    totals,
    errorTypeId: filters.errorTypeId,
    status: filters.status,
    filterEmployeeId,
    searchText: debouncedSearchText ?? '',
    bulkActions,
    tableSelectionKey,
    onSelectionChange,
    isRowSelectable,
    selectedIncidents,
    isBulkApproveModalOpen,
    onBulkApproveModalClose,
    onBulkApproveSuccess,
    isBulkRejectModalOpen,
    onBulkRejectModalClose,
    onBulkRejectSuccess,
    isSelectAllTotalActive,
    onSelectAllTotal,
    onClearSelectionTotal,
    selectAllTotalCount: totals.pendientes,
    excludeIncidentIds,
  };
}

export default useIncidentManagementList;
